import React, { useState } from 'react';
import { MapPin, TreePine, Users, Building2, Factory, AlertTriangle, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
interface CardProps {
  title: string;
  emoji: string;
  units: {
    name: string;
    topics: string[];
  }[];
  includes: string[];
  icon: React.ReactNode;
  iconBg: string;
  isOpen: boolean;
  onToggle: () => void;
}
function AccordionCard({
  title,
  emoji,
  units,
  includes,
  icon,
  iconBg,
  isOpen,
  onToggle
}: CardProps) {
  return <div className={`bg-gray-50 border rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${isOpen ? 'border-[#16a34a] ring-1 ring-[#16a34a]/30' : 'border-gray-200 hover:border-gray-300'}`} onClick={onToggle} data-id="element-205">
      <div className="p-3 sm:p-5" data-id="element-206">
        <div className="flex items-start gap-3 sm:gap-4" data-id="element-207">
          <div className={`${iconBg} w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 text-lg sm:text-xl`} data-id="element-208">
            {icon}
          </div>
          <div className="flex-1" data-id="element-209">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-0.5 leading-tight" data-id="element-210">
              {emoji} {title}
            </h3>
            <p className="text-[10px] sm:text-xs text-gray-400 font-medium" data-id="element-211">
              Tocá para ver el índice completo
            </p>
          </div>
          <div className="text-gray-400 mt-1" data-id="element-212">
            {isOpen ? <ChevronUp size={20} data-id="element-213" /> : <ChevronDown size={20} data-id="element-214" />}
          </div>
        </div>

        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100 mt-3 sm:mt-4' : 'max-h-0 opacity-0'}`} data-id="element-215">
          <div className="h-px bg-gray-200 mb-3 sm:mb-4" data-id="element-216"></div>

          <div className="space-y-3 sm:space-y-4" data-id="element-217">
            {units.map((unit, idx) => <div key={idx} data-id="element-218">
                <p className="text-xs sm:text-sm font-bold text-gray-800 mb-1" data-id="element-219">
                  {unit.name}
                </p>
                <ul className="space-y-0.5 sm:space-y-1 ml-3" data-id="element-220">
                  {unit.topics.map((topic, tidx) => <li key={tidx} className="text-[11px] sm:text-sm text-gray-500 flex items-start" data-id="element-221">
                      <span className="text-[#16a34a] mr-1.5" data-id="element-222">•</span>
                      {topic}
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Includes */}
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200" data-id="element-223">
            <p className="text-xs sm:text-sm font-bold text-[#16a34a] mb-1.5 flex items-center gap-1" data-id="element-224">
              <BookOpen className="w-3.5 h-3.5" data-id="element-225" /> Incluye:
            </p>
            <ul className="space-y-0.5 ml-3" data-id="element-226">
              {includes.map((item, iidx) => <li key={iidx} className="text-[11px] sm:text-sm text-gray-500 flex items-start" data-id="element-227">
                  <span className="text-[#16a34a] mr-1.5" data-id="element-228">✓</span>
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}
export function QueIncluye() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const cards = [{
    title: 'TOMO 1 — Organización del Territorio Argentino',
    emoji: '📕',
    icon: <MapPin className="text-red-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-229" />,
    iconBg: 'bg-red-100',
    units: [{
      name: 'Unidad 1: El territorio argentino',
      topics: ['Concepto de territorio', 'Construcción histórica del territorio', 'Soberanía y límites']
    }, {
      name: 'Unidad 2: División política',
      topics: ['Provincias y capitales', 'Regiones geográficas', 'Organización administrativa']
    }, {
      name: 'Unidad 3: Regiones de Argentina',
      topics: ['NOA, NEA, Cuyo, Pampeana, Patagonia', 'Características principales', 'Comparación entre regiones']
    }, {
      name: 'Unidad 4: Fronteras y relaciones territoriales',
      topics: ['Límites internacionales', 'Integración regional', 'Mercosur (introducción)']
    }],
    includes: ['Actividades de mapas', 'Análisis comparativos', 'Evaluación completa']
  }, {
    title: 'TOMO 2 — Ambientes y Recursos Naturales',
    emoji: '📗',
    icon: <TreePine className="text-green-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-230" />,
    iconBg: 'bg-green-100',
    units: [{
      name: 'Unidad 1: Ambientes naturales de Argentina',
      topics: ['Selvas, bosques, estepas', 'Características ambientales']
    }, {
      name: 'Unidad 2: Relieve argentino',
      topics: ['Montañas, llanuras, mesetas', 'Formación del relieve']
    }, {
      name: 'Unidad 3: Climas y biomas',
      topics: ['Tipos de clima en Argentina', 'Factores climáticos']
    }, {
      name: 'Unidad 4: Recursos naturales',
      topics: ['Renovables y no renovables', 'Uso y explotación']
    }, {
      name: 'Unidad 5: Problemáticas ambientales',
      topics: ['Deforestación', 'Contaminación', 'Cambio climático']
    }],
    includes: ['Análisis de casos reales', 'Actividades aplicadas', 'Evaluación + rúbricas']
  }, {
    title: 'TOMO 3 — Población Argentina',
    emoji: '📘',
    icon: <Users className="text-blue-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-231" />,
    iconBg: 'bg-blue-100',
    units: [{
      name: 'Unidad 1: Distribución de la población',
      topics: ['Densidad poblacional', 'Factores de distribución']
    }, {
      name: 'Unidad 2: Dinámica demográfica',
      topics: ['Natalidad, mortalidad', 'Crecimiento poblacional']
    }, {
      name: 'Unidad 3: Migraciones',
      topics: ['Internas y externas', 'Impacto social']
    }, {
      name: 'Unidad 4: Calidad de vida',
      topics: ['Indicadores sociales', 'Desigualdades']
    }],
    includes: ['Interpretación de gráficos', 'Actividades tipo PISA', 'Evaluaciones completas']
  }, {
    title: 'TOMO 4 — Espacios Urbanos y Rurales',
    emoji: '📙',
    icon: <Building2 className="text-orange-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-232" />,
    iconBg: 'bg-orange-100',
    units: [{
      name: 'Unidad 1: Espacios urbanos',
      topics: ['Características de las ciudades', 'Crecimiento urbano']
    }, {
      name: 'Unidad 2: Problemas urbanos',
      topics: ['Vivienda', 'Transporte', 'Servicios']
    }, {
      name: 'Unidad 3: Espacios rurales',
      topics: ['Actividades primarias', 'Organización rural']
    }, {
      name: 'Unidad 4: Relaciones campo-ciudad',
      topics: ['Intercambios', 'Transformaciones']
    }],
    includes: ['Estudios de caso', 'Actividades de análisis', 'Evaluación']
  }, {
    title: 'TOMO 5 — Economía y Territorio',
    emoji: '📕',
    icon: <Factory className="text-purple-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-233" />,
    iconBg: 'bg-purple-100',
    units: [{
      name: 'Unidad 1: Actividades económicas',
      topics: ['Sector primario, secundario, terciario']
    }, {
      name: 'Unidad 2: Circuitos productivos',
      topics: ['Soja', 'Ganadería', 'Industria']
    }, {
      name: 'Unidad 3: Economías regionales',
      topics: ['Producción por región', 'Características']
    }, {
      name: 'Unidad 4: Transporte y comercio',
      topics: ['Redes de transporte', 'Comercio interno y externo']
    }],
    includes: ['Análisis de casos reales', 'Actividades aplicadas', 'Evaluación completa']
  }, {
    title: 'TOMO 6 — Problemáticas Actuales',
    emoji: '📗',
    icon: <AlertTriangle className="text-amber-600 w-5 h-5 sm:w-7 sm:h-7" data-id="element-234" />,
    iconBg: 'bg-amber-100',
    units: [{
      name: 'Unidad 1: Desigualdades territoriales',
      topics: ['Regiones más desarrolladas vs menos']
    }, {
      name: 'Unidad 2: Problemas ambientales actuales',
      topics: ['Cambio climático', 'Crisis hídrica']
    }, {
      name: 'Unidad 3: Conflictos territoriales',
      topics: ['Uso de recursos', 'Impacto social']
    }, {
      name: 'Unidad 4: Argentina en el contexto global',
      topics: ['Globalización', 'Impacto en el territorio']
    }],
    includes: ['Debate', 'Análisis crítico', 'Evaluación final integradora']
  }];
  return <section id="que-incluye" className="py-8 sm:py-14 bg-white relative" data-id="element-235">
      <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-236">
        <div className="text-center mb-6 sm:mb-10" data-id="element-237">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-2 sm:mb-4" data-id="element-238">
            ¿Qué incluye el{' '}
            <span className="text-[#16a34a]" data-id="element-239">
              Kit Geografía Argentina Lista 2026
            </span>
            ?
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-3xl mx-auto" data-id="element-240">
            6 tomos completos con todo el contenido de Geografía Argentina
            organizado por unidades, con actividades, evaluaciones y material
            listo para usar en el aula.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5" data-id="element-241">
          {cards.map((card, index) => <AccordionCard key={index} {...card} isOpen={openIndex === index} onToggle={() => handleToggle(index)} data-id="element-242" />)}
        </div>
      </div>
    </section>;
}